/**
 * Notifications hooks with mock fallback. Reads fall back to the mock store; the
 * unread count is derived from the list (no dedicated count endpoint). Mark-read /
 * mark-all-read hit the API when authenticated, otherwise mutate the mock store.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { NotificationItem } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { notificationsApi } from "./notifications.api";
import { toNotification } from "./notifications.adapter";
import { useNotificationsStore } from "./notifications.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
}

export function useNotifications(): UseNotificationsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockItems = useNotificationsStore((s) => s.items);

  const query = useQuery({
    queryKey: queryKeys.notificationsList(LIST_PARAMS),
    queryFn: () => notificationsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { notifications: mockItems, isLoading: false, isError: false, source: "mock", refetch: query.refetch };
  if (query.data) return { notifications: query.data.data.map(toNotification), isLoading: false, isError: false, source: "api", refetch: query.refetch };
  if (query.isError) return { notifications: mockItems, isLoading: false, isError: true, source: "mock", refetch: query.refetch };
  return { notifications: [], isLoading: query.isLoading, isError: false, source: "api", refetch: query.refetch };
}

export function useUnreadNotificationsCount(): number {
  const { notifications } = useNotifications();
  return notifications.filter((n) => !n.read).length;
}

function useInvalidateNotifications() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_PREFIX });
}

export function useMarkNotificationRead() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const markRead = useNotificationsStore((s) => s.markRead);
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return notificationsApi.markRead(id);
      markRead(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useMarkAllNotificationsRead() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: async () => {
      if (authed) return notificationsApi.markAllRead();
      markAllRead();
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}
